/**
 * Document Card Component (Grid View)
 * Displays a single document in grid view with category-colored badges
 */
import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { File, Edit, Trash2, Eye, Link2, Ship, Lock } from 'lucide-react';
import { getDocumentTypeLabel, getCategoryForType } from './documentCategories';

const DocumentCard = ({ doc, onEdit, onDelete, onPreview, onLink, onSavePassword, categoryColor }) => {
  const category = getCategoryForType(doc.document_type);
  const isPdf = doc.file_name?.toLowerCase().endsWith('.pdf');

  return (
    <Card className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`document-card-${doc.id}`}>
      <CardContent className="p-4">
        <div 
          className="flex items-start justify-between cursor-pointer" 
          onClick={() => onPreview && onPreview(doc)}
          data-testid={`preview-trigger-${doc.id}`}
        >
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${category.bgColor} ${category.borderColor} border`}>
              <File className={`h-5 w-5 ${category.textColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-white truncate" title={doc.file_name}>
                {doc.file_name}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge 
                  variant="outline" 
                  className={`${category.bgColor} ${category.borderColor} ${category.textColor} text-xs`}
                >
                  {getDocumentTypeLabel(doc.document_type)}
                </Badge>
                <span className="text-xs text-slate-500">{doc.tax_year}</span>
              </div>
              {doc.linked_trip && (
                <div className="flex items-center gap-1.5 mt-1.5" data-testid={`linked-trip-badge-${doc.id}`}>
                  <Ship className="h-3 w-3 text-amber-400" />
                  <span className="text-xs text-amber-400 truncate">
                    {doc.linked_trip.vessel_name || doc.linked_trip.trip_name || 'Linked Trip'}
                  </span>
                </div>
              )}
              {doc.month && (
                <p className="text-xs text-slate-500 mt-1">
                  Period: {doc.month}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPreview && onPreview(doc)}
            className="flex-1 border-slate-600 text-slate-300 hover:text-amber-400 hover:border-amber-500/50"
            data-testid={`view-document-${doc.id}`}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onLink && onLink(doc); }}
            className={`flex-1 border-slate-600 text-slate-300 hover:text-amber-400 hover:border-amber-500/50 ${doc.linked_trip_id ? 'border-amber-500/30 text-amber-400' : ''}`}
            data-testid={`link-document-${doc.id}`}
          >
            <Link2 className="h-4 w-4 mr-1" />
            {doc.linked_trip_id ? 'Linked' : 'Link'}
          </Button>
          {isPdf && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onSavePassword && onSavePassword(doc); }}
              className={`border-slate-600 ${doc.has_saved_password ? 'text-sky-400 border-sky-500/30' : 'text-slate-400 hover:text-amber-400 hover:border-amber-500/50'}`}
              title={doc.has_saved_password ? 'Password saved — click to update' : 'Save PDF password'}
              data-testid={`save-password-${doc.id}`}
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(doc)}
            className="border-slate-600 text-slate-300 hover:text-white"
            data-testid={`edit-document-${doc.id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(doc.id)}
            className="border-red-600/50 text-red-400 hover:text-red-300 hover:bg-red-600/10"
            data-testid={`delete-document-${doc.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentCard;
